import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Fragment, useEffect, useRef, useState } from "react";
import { FileRejection } from "react-dropzone";
import { toast } from "react-toastify";
import {
  getCompanyDocumentError,
  putCompanyDocumentToS3,
} from "../../api/companyDocuments";
import {
  useCompleteCompanyDocumentUploads,
  usePresignCompanyDocumentUploads,
} from "../../queries/companyDocuments";
import { PresignedCompanyDocumentUpload } from "../../types/companyDocuments";
import {
  createCompanyDocumentClientId,
  formatCompanyDocumentSize,
} from "../../utils/companyDocuments";
import DocumentDropzone from "./DocumentDropzone";
import DocumentFileTypeIcon, {
  getDocumentFilePresentation,
} from "./DocumentFileTypeIcon";

const MAX_FILES = 50;
const MAX_FILE_SIZE = 100 * 1024 * 1024;
const MAX_CONCURRENT_UPLOADS = 3;

type UploadStatus =
  | "queued"
  | "uploading"
  | "confirming"
  | "done"
  | "failed"
  | "cancelled";

interface UploadQueueItem {
  clientId: string;
  file: File;
  status: UploadStatus;
  progress: number;
  error?: string;
  documentId?: string;
}

interface DocumentUploadDialogProps {
  open: boolean;
  companyId: string;
  folderId: string | null;
  targetBreadcrumb: string[];
  onClose: () => void;
  onUploadsCompleted: () => void;
}

function contentTypeForFile(file: File): string {
  if (file.type) return file.type;
  const extension = file.name.split(".").pop()?.toLowerCase();
  const extensionTypes: Record<string, string> = {
    csv: "text/csv",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    gif: "image/gif",
    jpeg: "image/jpeg",
    jpg: "image/jpeg",
    pdf: "application/pdf",
    png: "image/png",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    txt: "text/plain",
    webp: "image/webp",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  };
  return extensionTypes[extension || ""] || "";
}

function isCancellation(error: unknown) {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { code?: string }).code === "ERR_CANCELED"
  );
}

async function runWithConcurrency(
  tasks: Array<() => Promise<void>>,
  concurrency: number
) {
  let nextTask = 0;
  const worker = (): Promise<void> => {
    if (nextTask >= tasks.length) return Promise.resolve();
    const task = tasks[nextTask];
    nextTask += 1;
    return task().then(worker);
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, tasks.length) }, () => worker())
  );
}

function statusText(item: UploadQueueItem) {
  if (item.status === "uploading") return `Uploading ${item.progress}%`;
  if (item.status === "confirming") return "Confirming upload";
  if (item.status === "done") return "Uploaded";
  if (item.status === "cancelled") return "Cancelled";
  if (item.status === "failed") return item.error || "Upload failed";
  return "Ready to upload";
}

export default function DocumentUploadDialog({
  open,
  companyId,
  folderId,
  targetBreadcrumb,
  onClose,
  onUploadsCompleted,
}: DocumentUploadDialogProps) {
  const [items, setItems] = useState<UploadQueueItem[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dropzoneKey, setDropzoneKey] = useState(0);
  const itemsRef = useRef<UploadQueueItem[]>([]);
  const wasOpen = useRef(false);
  const targetRef = useRef({ folderId, breadcrumb: targetBreadcrumb });
  const uploadInProgressRef = useRef(false);
  const cancellationIds = useRef(new Set<string>());
  const abortControllers = useRef(new Map<string, AbortController>());
  const presignMutation = usePresignCompanyDocumentUploads();
  const completeMutation = useCompleteCompanyDocumentUploads(companyId);

  const replaceItems = (nextItems: UploadQueueItem[]) => {
    itemsRef.current = nextItems;
    setItems(nextItems);
  };

  const updateItem = (clientId: string, changes: Partial<UploadQueueItem>) => {
    replaceItems(
      itemsRef.current.map((item) =>
        item.clientId === clientId ? { ...item, ...changes } : item
      )
    );
  };

  useEffect(() => {
    if (open && !wasOpen.current) {
      targetRef.current = { folderId, breadcrumb: targetBreadcrumb };
      replaceItems([]);
      cancellationIds.current.clear();
      abortControllers.current.clear();
      setValidationError(null);
      setDropzoneKey((current) => current + 1);
    }
    wasOpen.current = open;
  }, [folderId, open, targetBreadcrumb]);

  const addFiles = (files: File[]) => {
    const activeCount = itemsRef.current.filter(
      (item) => item.status !== "done" && item.status !== "cancelled"
    ).length;
    const availableSlots = Math.max(MAX_FILES - activeCount, 0);
    const accepted: UploadQueueItem[] = [];
    const errors: string[] = [];

    files.slice(0, availableSlots).forEach((file) => {
      if (!file.name.trim() || file.name.length > 255) {
        errors.push(`${file.name || "This file"} has an invalid filename.`);
      } else if (file.size === 0 || file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name} must be between 1 byte and 100 MB.`);
      } else if (!contentTypeForFile(file)) {
        errors.push(`${file.name} has an unsupported file type.`);
      } else {
        accepted.push({
          clientId: createCompanyDocumentClientId(),
          file,
          status: "queued",
          progress: 0,
        });
      }
    });

    if (files.length > availableSlots) {
      errors.push(`A maximum of ${MAX_FILES} files can be uploaded at once.`);
    }
    if (accepted.length) replaceItems([...itemsRef.current, ...accepted]);
    setValidationError(errors.length ? errors.join(" ") : null);
  };

  const handleRejections = (rejections: FileRejection[]) => {
    const names = rejections.map((rejection) => rejection.file.name).join(", ");
    setValidationError(
      names
        ? `${names} could not be added. Check the file type, file size, and selection limit.`
        : "Some files could not be added."
    );
  };

  const uploadItem = async (
    descriptor: PresignedCompanyDocumentUpload,
    item: UploadQueueItem
  ) => {
    if (cancellationIds.current.has(item.clientId)) return;
    const controller = new AbortController();
    abortControllers.current.set(item.clientId, controller);
    updateItem(item.clientId, {
      documentId: descriptor.documentId,
      error: undefined,
      progress: 0,
      status: "uploading",
    });

    try {
      await putCompanyDocumentToS3({
        uploadUrl: descriptor.uploadUrl,
        file: item.file,
        headers: descriptor.headers,
        signal: controller.signal,
        onProgress: (progress) => updateItem(item.clientId, { progress }),
      });
      if (cancellationIds.current.has(item.clientId)) return;

      updateItem(item.clientId, { progress: 100, status: "confirming" });
      const completion = await completeMutation.mutateAsync({
        uploads: [
          {
            documentId: descriptor.documentId,
            completionToken: descriptor.completionToken,
          },
        ],
      });
      const result = [
        ...(completion.results || []),
        ...(completion.uploads || []),
      ].find((entry) => entry.documentId === descriptor.documentId);
      if (!result || result.success === false || result.status === "FAILED") {
        throw new Error(result?.error || "The upload could not be confirmed.");
      }
      updateItem(item.clientId, { progress: 100, status: "done" });
    } catch (error) {
      if (cancellationIds.current.has(item.clientId) || isCancellation(error)) {
        updateItem(item.clientId, { status: "cancelled" });
      } else {
        updateItem(item.clientId, {
          error: getCompanyDocumentError(error),
          status: "failed",
        });
      }
    } finally {
      abortControllers.current.delete(item.clientId);
    }
  };

  const startUpload = async (requestedIds?: string[]) => {
    if (uploadInProgressRef.current) return;
    const queuedItems = itemsRef.current.filter(
      (item) =>
        item.status === "queued" &&
        (!requestedIds || requestedIds.includes(item.clientId))
    );
    if (!queuedItems.length) return;

    uploadInProgressRef.current = true;
    setIsUploading(true);
    setValidationError(null);
    let completedAny = false;
    try {
      const target = targetRef.current;
      const presigned = await presignMutation.mutateAsync({
        folderId: target.folderId,
        files: queuedItems.map((item) => ({
          clientId: item.clientId,
          name: item.file.name,
          contentType: contentTypeForFile(item.file),
          size: item.file.size,
        })),
      });
      const descriptors = new Map(
        presigned.uploads.map((descriptor) => [descriptor.clientId, descriptor])
      );
      const tasks = queuedItems.map((item) => async () => {
        const descriptor = descriptors.get(item.clientId);
        if (!descriptor) {
          updateItem(item.clientId, {
            error: "The upload URL was not returned for this file.",
            status: "failed",
          });
          return;
        }
        await uploadItem(descriptor, item);
        if (
          itemsRef.current.find((current) => current.clientId === item.clientId)
            ?.status === "done"
        ) {
          completedAny = true;
        }
      });
      await runWithConcurrency(tasks, MAX_CONCURRENT_UPLOADS);
    } catch (error) {
      queuedItems.forEach((item) => {
        if (item.status === "queued") {
          updateItem(item.clientId, {
            error: getCompanyDocumentError(error),
            status: "failed",
          });
        }
      });
    } finally {
      uploadInProgressRef.current = false;
      setIsUploading(false);
      if (completedAny) {
        // Remount the file input after a completed browser selection. This
        // permits selecting the same filename again without relying on
        // browser-specific hidden-input reset behavior.
        setDropzoneKey((current) => current + 1);
        onUploadsCompleted();
        toast("Uploaded files are now available in this folder.", {
          type: "success",
          autoClose: 2500,
        });
      }
    }
  };

  const cancelItem = (clientId: string) => {
    cancellationIds.current.add(clientId);
    abortControllers.current.get(clientId)?.abort();
    const item = itemsRef.current.find(
      (current) => current.clientId === clientId
    );
    if (item?.status === "queued")
      updateItem(clientId, { status: "cancelled" });
  };

  const removeItem = (clientId: string) => {
    cancelItem(clientId);
    replaceItems(itemsRef.current.filter((item) => item.clientId !== clientId));
  };

  const retryItem = (clientId: string) => {
    cancellationIds.current.delete(clientId);
    updateItem(clientId, {
      documentId: undefined,
      error: undefined,
      progress: 0,
      status: "queued",
    });
    startUpload([clientId]).catch(() => undefined);
  };

  const closeDialog = () => {
    if (isUploading) return;
    replaceItems([]);
    setValidationError(null);
    onClose();
  };

  const queuedCount = items.filter((item) => item.status === "queued").length;
  const completedCount = items.filter((item) => item.status === "done").length;
  const targetLabel = targetRef.current.breadcrumb.join(" / ");
  const uploadSummary = !items.length
    ? "Add files to begin"
    : isUploading
    ? "Upload in progress"
    : queuedCount
    ? `${queuedCount} ${
        queuedCount === 1 ? "file is" : "files are"
      } ready to upload`
    : completedCount
    ? `${completedCount} ${
        completedCount === 1 ? "file has" : "files have"
      } been uploaded`
    : "Review files in the upload queue";

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeDialog}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/30" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-xl">
                <div className="flex items-start justify-between border-b border-gray-100 px-6 py-4">
                  <div>
                    <Dialog.Title className="text-base font-semibold text-gray-900">
                      Upload files
                    </Dialog.Title>
                    <p className="mt-1 truncate text-xs text-gray-500">
                      Destination: {targetLabel || "Company Documents"}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={isUploading}
                    onClick={closeDialog}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Close upload dialog</span>
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="space-y-4 p-6">
                  <DocumentDropzone
                    key={dropzoneKey}
                    disabled={isUploading}
                    maxFiles={MAX_FILES}
                    maxSize={MAX_FILE_SIZE}
                    onFilesAdded={addFiles}
                    onFilesRejected={handleRejections}
                  />
                  <div className="flex items-start gap-2 rounded-md bg-gray-50 px-3 py-2.5 text-xs text-gray-600">
                    <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <span>
                      Add up to {MAX_FILES} files, with a maximum size of 100 MB
                      per file. Files upload directly to secure storage.
                    </span>
                  </div>
                  {validationError && (
                    <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
                      {validationError}
                    </p>
                  )}
                  {items.length > 0 && (
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-800">
                          Upload queue
                        </p>
                        <span className="text-xs text-gray-500">
                          {items.length} {items.length === 1 ? "file" : "files"}
                        </span>
                      </div>
                      <ul className="max-h-80 space-y-2 overflow-y-auto">
                        {items.map((item) => {
                          const presentation = getDocumentFilePresentation(
                            item.file.name
                          );
                          return (
                            <li
                              key={item.clientId}
                              className="flex min-h-[76px] items-center gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3.5 shadow-sm"
                            >
                              <div
                                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${presentation.surfaceClassName}`}
                              >
                                <DocumentFileTypeIcon
                                  fileName={item.file.name}
                                  size={24}
                                />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-3">
                                  <p className="truncate text-sm font-medium leading-5 text-gray-800">
                                    {item.file.name}
                                  </p>
                                  <span className="shrink-0 text-xs text-gray-500">
                                    {formatCompanyDocumentSize(item.file.size)}
                                  </span>
                                </div>
                                <div className="mt-1.5 flex min-h-[20px] items-center gap-2">
                                  <span
                                    className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${presentation.badgeClassName}`}
                                  >
                                    {presentation.extension}
                                  </span>
                                  {(item.status === "uploading" ||
                                    item.status === "confirming") && (
                                    <div className="h-1.5 min-w-[72px] flex-1 overflow-hidden rounded bg-gray-100">
                                      <div
                                        className="h-full bg-primary transition-all"
                                        style={{ width: `${item.progress}%` }}
                                      />
                                    </div>
                                  )}
                                  {item.status === "done" && (
                                    <CheckCircleIcon className="h-4 w-4 shrink-0 text-green-600" />
                                  )}
                                  <span
                                    className={`min-w-0 truncate text-xs ${
                                      item.status === "failed"
                                        ? "text-red-600"
                                        : item.status === "done"
                                        ? "text-green-700"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    {statusText(item)}
                                  </span>
                                </div>
                              </div>
                              <div className="flex shrink-0 items-center gap-1">
                                {item.status === "failed" && !isUploading && (
                                  <button
                                    type="button"
                                    onClick={() => retryItem(item.clientId)}
                                    className="rounded p-1.5 text-primary hover:bg-primary/10"
                                    aria-label={`Retry ${item.file.name}`}
                                    title="Retry upload"
                                  >
                                    <ArrowPathIcon className="h-4 w-4" />
                                  </button>
                                )}
                                {(item.status === "queued" ||
                                  item.status === "uploading" ||
                                  item.status === "confirming") && (
                                  <button
                                    type="button"
                                    onClick={() => cancelItem(item.clientId)}
                                    className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                                    aria-label={`Cancel ${item.file.name}`}
                                    title="Cancel upload"
                                  >
                                    <XMarkIcon className="h-4 w-4" />
                                  </button>
                                )}
                                {!isUploading &&
                                  (item.status === "queued" ||
                                    item.status === "failed" ||
                                    item.status === "cancelled") && (
                                    <button
                                      type="button"
                                      onClick={() => removeItem(item.clientId)}
                                      className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                                      aria-label={`Remove ${item.file.name}`}
                                      title="Remove file"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between gap-4 border-t border-gray-100 px-6 py-4">
                  <button
                    type="button"
                    onClick={closeDialog}
                    disabled={isUploading}
                    className="rounded-sm border border-primary px-3 py-2 text-sm font-semibold text-primary hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Close
                  </button>
                  <div className="flex items-center gap-3">
                    <span className="hidden text-xs text-gray-500 sm:block">
                      {uploadSummary}
                    </span>
                    <button
                      type="button"
                      disabled={queuedCount === 0 || isUploading || !companyId}
                      onClick={() => {
                        startUpload().catch(() => undefined);
                      }}
                      className="rounded-sm bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isUploading
                        ? "Uploading…"
                        : `Upload${
                            queuedCount
                              ? ` ${queuedCount} file${
                                  queuedCount === 1 ? "" : "s"
                                }`
                              : ""
                          }`}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
